import type { StatusStyle } from "@/components/ui/status-badge";

export type ShipmentStatus = "pending" | "in_transit" | "delivered" | "cancelled";

const SHIPMENT_STATUS_LABELS: Record<ShipmentStatus, string> = {
  pending: "Pending",
  in_transit: "In Transit",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

/**
 * Sanctioned status palette (DESIGN.md §1/§7):
 *  - pending    → amber   (pending / attention)
 *  - in_transit → cyan    (in-motion)
 *  - delivered  → emerald (done)
 *  - cancelled  → red     (mirrors the PO `cancelled` colour so a cancelled
 *                          row reads identically across PO + Shipments)
 *
 * The `hover:` clause keeps the fill stable when the map is applied to a
 * shadcn `SelectTrigger` (which otherwise gets a hover background).
 */
const SHIPMENT_STATUS_CLASS: Record<ShipmentStatus, string> = {
  pending: "bg-amber-100 text-amber-800 hover:bg-amber-100",
  in_transit: "bg-cyan-100 text-cyan-800 hover:bg-cyan-100",
  delivered: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100",
  cancelled: "bg-red-100 text-red-800 hover:bg-red-100",
};

/** Resolve a shipment status into a {@link StatusStyle} for `<StatusBadge>`. */
export function shipmentStatusStyle(status: ShipmentStatus): StatusStyle {
  return {
    label: SHIPMENT_STATUS_LABELS[status],
    className: SHIPMENT_STATUS_CLASS[status],
  };
}

/** Colour-only accessor for surfaces that style their own element (e.g. the
 *  inline status `SelectTrigger`) rather than rendering a `<StatusBadge>`. */
export function shipmentStatusClassName(status: ShipmentStatus): string {
  return SHIPMENT_STATUS_CLASS[status];
}
